import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Modal from "./Modal";
import api from "../../services/api";
import { DEFAULT_FEATURES } from "../../hooks/useFeatures";

const getPlanFeatures = (plan) => {
  const planFeatures = DEFAULT_FEATURES[plan];

  if (!planFeatures) return [];

  return Object.keys(planFeatures).filter((key) => planFeatures[key] === true);
};

const PLAN_TIERS = {
  premium: {
    features: getPlanFeatures("premium"),
    name: "Premium",
    price: "$99/month",
  },
  pro: {
    features: getPlanFeatures("pro"),
    name: "Pro",
    price: "$299/month",
  },
  ultimate: {
    features: getPlanFeatures("ultimate"),
    name: "Ultimate",
    price: "$999/month",
  },
};

export default function ModalTierRecommendation({ usedFeatures = [], close }) {
  const { t } = useTranslation();

  const recommendedTier = useMemo(() => {
    if (!usedFeatures || usedFeatures.length === 0) {
      return "premium";
    }

    const ultimateOnlyFeatures = PLAN_TIERS.ultimate.features.filter((f) => !PLAN_TIERS.pro.features.includes(f));
    if (usedFeatures.some((f) => ultimateOnlyFeatures.includes(f))) {
      return "ultimate";
    }

    const proOnlyFeatures = PLAN_TIERS.pro.features.filter((f) => !PLAN_TIERS.premium.features.includes(f));
    if (usedFeatures.some((f) => proOnlyFeatures.includes(f))) {
      return "pro";
    }

    return "premium";
  }, [usedFeatures]);

  const recommendedPlan = PLAN_TIERS[recommendedTier];

  const usedFeaturesFiltered = useMemo(() => [...new Set(usedFeatures)].filter((f) => f), [usedFeatures]);

  const handleViewPlans = () => {
    window.open("https://lucashealth.ai/pricing", "_blank");
  };

  const handleContinue = async () => {
    try {
      const data = await api.getStripePaymentLink(window.location.href, window.location.href);
      window.location.assign(data.data.url);
    } catch (error) {
      console.error("Failed to get Stripe payment link:", error);
    }
  };

  return (
    <Modal
      show={true}
      close={close}
      size="medium"
      className="tier-recommendation-modal"
      action={
        <>
          <button className="white" onClick={handleViewPlans}>
            {t("tierRecommendation.seePlans")}
          </button>
          <button className="blue" onClick={handleContinue}>
            {t("tierRecommendation.continue")}
            <i className="material-icons ml-5">chevron_right</i>
          </button>
        </>
      }
    >
      <div className="tier-recommendation-wrapper">
        {usedFeaturesFiltered.length > 0 && (
          <div className="features-used-section">
            <h3>{t("tierRecommendation.featuresYouUsed")}</h3>
            <p className="features-used-description">{t("tierRecommendation.basedOnYourNotes")}</p>
            <div className="features-list">
              {usedFeaturesFiltered.map((feature, index) => (
                <div key={index} className="feature-item">
                  <i className="material-icons">check_circle</i>
                  <div className="feature-info">
                    <div className="feature-name">{t(`upgradeRequired.features.${feature}.title`, feature)}</div>
                    <div className="feature-description">{t(`upgradeRequired.features.${feature}.description`, "")}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="recommended-plan-section">
          <h3>{t("tierRecommendation.recommendedPlan")}</h3>
          <div className="plan-card recommended">
            <div className="plan-header">
              <div className="plan-name">{recommendedPlan.name}</div>
              <div className="plan-badge">{t("tierRecommendation.recommended")}</div>
            </div>
            <div className="plan-price">{recommendedPlan.price}</div>
            <div className="plan-features">
              <p className="plan-features-title">{t("tierRecommendation.includesAllFeatures")}</p>
              <ul>
                {recommendedPlan.features.slice(0, 5).map((feature, index) => (
                  <li key={index}>
                    <i className="material-icons">check</i>
                    {t(`upgradeRequired.features.${feature}.title`, feature)}
                  </li>
                ))}
                {recommendedPlan.features.length > 5 && <li className="more-features">{t("tierRecommendation.andMore")}</li>}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
